import { useCallback, useEffect, useState } from 'react'
import useWindowSize from 'react-use/esm/useWindowSize'
import { SimpleTextarea } from '~/components/common/SimpleTextarea'
import { useCompactViewport } from '~/hooks/useCompactViewport'
import { AgentRequest } from '~/protos/agents'
import { AppEvents, globalEventEmitter } from '~/utils/shared'

export interface TaskParams {
  uuid?: string
  request: AgentRequest
  resolve?: (success: boolean) => void
  submitBlockedReason?: string
}

interface BaseTaskFormProps {
  onChange: (task: TaskParams) => void
  enter2Submit?: boolean
  label?: string
  minRows?: number
  maxRows?: number
  placeholder?: string
  defaultValue?: string
  inputClassName?: string
  tooltipTargetClassName?: string
  disabled?: boolean
  required?: boolean
}

export function BaseTaskForm({
  onChange,
  enter2Submit,
  label = 'Description',
  minRows,
  maxRows,
  placeholder,
  defaultValue,
  inputClassName,
  tooltipTargetClassName,
  disabled = false,
  required = true
}: BaseTaskFormProps) {
  const windowSize = useWindowSize()
  const isPlugin = useCompactViewport()
  const rows = minRows || (isPlugin ? 4 : Math.floor(windowSize.height / 100))
  const [value, setValue] = useState(defaultValue)

  // Sync internal state when defaultValue changes (e.g., when switching between example and own)
  useEffect(() => {
    setValue(defaultValue)
  }, [defaultValue])

  useEffect(() => {
    // When the field is optional, seed an (empty) request on mount so the task
    // is submittable without typing anything (e.g. Azure Migrate + Deploy, where
    // the selected repository is the real input).
    if (defaultValue || !required) {
      onChange({
        request: AgentRequest.fromPartial({ command: { commandString: defaultValue ?? '' } })
      })
    }
  }, [defaultValue, onChange, required])

  const handleChange = useCallback(
    (text: string) => {
      setValue(text)
      onChange({ request: AgentRequest.fromPartial({ command: { commandString: text } }) })
    },
    [onChange]
  )

  useEffect(() => {
    const handleSetValue = (text: string) => {
      handleChange(text)
    }

    globalEventEmitter.on(AppEvents.SetTextAreaValue, handleSetValue)

    return () => {
      globalEventEmitter.off(AppEvents.SetTextAreaValue, handleSetValue)
    }
  }, [handleChange])

  return (
    <>
      <div className='label font-semibold label-lg-bold text-default-700 mb-[10px] select-none'>
        {label}:<span className='text-tokens-status-error-text'> {required ? '*' : ''}</span>
      </div>
      <div
        className={`tool-tip-target ${tooltipTargetClassName} w-[1px] h-[1px] relative top-[50px] ${isPlugin ? 'left-[200px]' : 'left-[400px]'}`}
      ></div>
      <SimpleTextarea
        value={value}
        onChange={handleChange}
        enter2Submit={enter2Submit}
        minRows={rows}
        maxRows={maxRows}
        placeholder={placeholder || 'Describe the task...'}
        inputClassName={inputClassName}
        disabled={disabled}
      />
    </>
  )
}
